/* eslint-disable no-async-promise-executor */
/* eslint-disable @typescript-eslint/no-inferrable-types */

import { IDBTransactionMode } from "@/enums/enums";
import { DecryptionIndexedDbCash, EncryptionIndexedDbCash } from "@/helper/shareHelper";
import { SelectedMerchantModel } from "@/model/merchant/selectedMerchantModel";
import { UserModel } from "@/model/user/userModel";

const indexedDB: IDBFactory = window.indexedDB;

const CacheDBName: string = "CacheDB";
const CacheDBVersion: number = 3; // must increase when add or modify any object stores
const ObjectStore: any = {
  AccessToken: "AccessToken",
  UserProfile: "UserProfile",
  SelectedMerchant: "SelectedMerchant",
  ContactTaxIdLogo: "ContactTaxIdLogo",
};
const ObjectStoresDefaultCount: number = Object.keys(ObjectStore).length;

const GetCurrentCacheDBVersion = (): Promise<number> => {
  return new Promise((resolve, reject) => {
    const request: IDBOpenDBRequest = indexedDB.open(CacheDBName);

    request.onsuccess = (e: any) => {
      const db: IDBDatabase = e.target.result;
      const objectStoresCount: number = db.objectStoreNames.length;
      db.close();
      resolve(objectStoresCount !== ObjectStoresDefaultCount ? 0 : db.version);
    };

    request.onerror = (e: any) => {
      reject(0);
    };
  });
};

const DeleteCacheIndexedDB = (): Promise<boolean> => {
  return new Promise((resolve, reject) => {
    const request: IDBOpenDBRequest = indexedDB.deleteDatabase(CacheDBName);

    request.onsuccess = (e: any) => {
      resolve(true);
    };

    request.onerror = (e: any) => {
      console.error("Failed to delete database CacheDB:", e.target.error);
      resolve(false);
    };

    request.onblocked = (e: any) => {
      console.warn("Deletion of database CacheDB is blocked, possibly because it's open in another tab or process.");
      resolve(false);
    };
  });
};

const OpenCacheIndexedDB = (): Promise<IDBDatabase> => {
  return new Promise(async (resolve, reject) => {
    if (!indexedDB) {
      reject(new Error("Your browser doesn't support IndexedBD"));
      return;
    }

    const currentCacheDBVersion: number = await GetCurrentCacheDBVersion();

    if (currentCacheDBVersion !== CacheDBVersion) {
      await DeleteCacheIndexedDB();
    }

    const request: IDBOpenDBRequest = indexedDB.open(CacheDBName, CacheDBVersion);

    request.onerror = (e: any) => {
      console.error(`CacheIndexedDB Error ${e.target.error}`);
      reject(e.target.error);
    };

    request.onsuccess = (e: any) => {
      const db: IDBDatabase = e.target.result;
      resolve(db);
    };

    request.onupgradeneeded = (e: any) => {
      const db: IDBDatabase = e.target.result;

      for (const [key, value] of Object.entries(ObjectStore)) {
        if (!db.objectStoreNames.contains(value as string)) {
          db.createObjectStore(value as string, { keyPath: null, autoIncrement: false });
        }
      }
    };
  });
};

// #region AccessToken
export const AddAccessTokenToIndexedDB = (key: IDBValidKey, accessToken: string): Promise<boolean> => {
  return new Promise(async (resolve, reject) => {
    const db: IDBDatabase = await OpenCacheIndexedDB();

    if (db) {
      const objectStore: IDBObjectStore = db
        .transaction([ObjectStore.AccessToken], IDBTransactionMode.ReadWrite)
        .objectStore(ObjectStore.AccessToken);
      const encryptedAccessToken: string = EncryptionIndexedDbCash(JSON.stringify(accessToken));
      const request: IDBRequest = objectStore.put(encryptedAccessToken, key);

      request.onsuccess = (e: any) => {
        db.close();
        resolve(true);
      };

      request.onerror = (e: any) => {
        console.error(`Error to request AddAccessTokenToIndexedDB ${e.target.error}`);
        db.close();
        resolve(false);
      };
    } else {
      resolve(false);
    }
  });
};

export const GetAccessTokenFromIndexedDB = (key: IDBValidKey): Promise<string | null> => {
  return new Promise(async (resolve, reject) => {
    const db: IDBDatabase = await OpenCacheIndexedDB();

    if (db) {
      const objectStore: IDBObjectStore = db
        .transaction([ObjectStore.AccessToken], IDBTransactionMode.ReadOnly)
        .objectStore(ObjectStore.AccessToken);
      const request: IDBRequest = objectStore.get(key);

      request.onsuccess = (e: any) => {
        db.close();

        if (request.result) {
          const accessToken: string = JSON.parse(DecryptionIndexedDbCash(request.result));
          resolve(accessToken);
        } else {
          resolve(null);
        }
      };

      request.onerror = (e: any) => {
        console.error(`Error to request GetAccessTokenFromIndexedDB ${e.target.error}`);
        db.close();
        resolve(null);
      };
    } else {
      resolve(null);
    }
  });
};

export const ClearAccessTokenFromIndexedDB = (): Promise<boolean> => {
  return new Promise(async (resolve, reject) => {
    const db: IDBDatabase = await OpenCacheIndexedDB();

    if (db) {
      const objectStore: IDBObjectStore = db
        .transaction([ObjectStore.AccessToken], IDBTransactionMode.ReadWrite)
        .objectStore(ObjectStore.AccessToken);
      const request: IDBRequest = objectStore.clear();

      request.onerror = (e: any) => {
        db.close();
        resolve(true);
      };

      request.onsuccess = (e: any) => {
        db.close();
        resolve(false);
      };
    } else {
      resolve(false);
    }
  });
};
// #endregion

// #region UserProfile
export const AddUserProfileToIndexedDB = (key: IDBValidKey, userProfile: UserModel): Promise<boolean> => {
  return new Promise(async (resolve, reject) => {
    const db: IDBDatabase = await OpenCacheIndexedDB();

    if (db) {
      const objectStore: IDBObjectStore = db
        .transaction([ObjectStore.UserProfile], IDBTransactionMode.ReadWrite)
        .objectStore(ObjectStore.UserProfile);
      const encryptedUserProfile: string = EncryptionIndexedDbCash(JSON.stringify(userProfile));
      const request: IDBRequest = objectStore.put(encryptedUserProfile, key);

      request.onsuccess = (e: any) => {
        db.close();
        resolve(true);
      };

      request.onerror = (e: any) => {
        console.error(`Error to request AddUserProfileToIndexedDB ${e.target.error}`);
        db.close();
        resolve(false);
      };
    } else {
      resolve(false);
    }
  });
};

export const GetUserProfileFromIndexedDB = (key: IDBValidKey): Promise<UserModel | null> => {
  return new Promise(async (resolve, reject) => {
    const db: IDBDatabase = await OpenCacheIndexedDB();

    if (db) {
      const objectStore: IDBObjectStore = db
        .transaction([ObjectStore.UserProfile], IDBTransactionMode.ReadOnly)
        .objectStore(ObjectStore.UserProfile);
      const request: IDBRequest = objectStore.get(key);

      request.onsuccess = (e: any) => {
        db.close();

        if (request.result) {
          const userProfile: UserModel = JSON.parse(DecryptionIndexedDbCash(request.result));
          resolve(userProfile);
        } else {
          resolve(null);
        }
      };

      request.onerror = (e: any) => {
        console.error(`Error to request GetUserProfileFromIndexedDB ${e.target.error}`);
        db.close();
        resolve(null);
      };
    } else {
      resolve(null);
    }
  });
};

export const DeleteUserProfileFromIndexedDB = (key: IDBValidKey): Promise<boolean> => {
  return new Promise(async (resolve, reject) => {
    const db: IDBDatabase = await OpenCacheIndexedDB();

    if (db) {
      const objectStore: IDBObjectStore = db
        .transaction([ObjectStore.UserProfile], IDBTransactionMode.ReadWrite)
        .objectStore(ObjectStore.UserProfile);
      const request: IDBRequest = objectStore.delete(key);

      request.onsuccess = (e: any) => {
        db.close();
        resolve(true);
      };

      request.onerror = (e: any) => {
        console.error(`Error to request DeleteUserProfileFromIndexedDB ${e.target.error}`);
        db.close();
        resolve(false);
      };
    } else {
      resolve(false);
    }
  });
};

export const ClearUserProfileFromIndexedDB = (): Promise<boolean> => {
  return new Promise(async (resolve, reject) => {
    const db: IDBDatabase = await OpenCacheIndexedDB();

    if (db) {
      const objectStore: IDBObjectStore = db
        .transaction([ObjectStore.UserProfile], IDBTransactionMode.ReadWrite)
        .objectStore(ObjectStore.UserProfile);
      const request: IDBRequest = objectStore.clear();

      request.onsuccess = (e: any) => {
        db.close();
        resolve(true);
      };

      request.onerror = (e: any) => {
        db.close();
        resolve(false);
      };
    } else {
      resolve(false);
    }
  });
};
// #endregion

// #region SelectedMerchant
export const AddSelectedMerchantToIndexedDB = (
  key: IDBValidKey,
  selectedMerchant: SelectedMerchantModel
): Promise<boolean> => {
  return new Promise(async (resolve, reject) => {
    const db: IDBDatabase = await OpenCacheIndexedDB();

    if (db) {
      const objectStore: IDBObjectStore = db
        .transaction([ObjectStore.SelectedMerchant], IDBTransactionMode.ReadWrite)
        .objectStore(ObjectStore.SelectedMerchant);
      const encryptedSelectedMerchant: string = EncryptionIndexedDbCash(JSON.stringify(selectedMerchant));
      const request: IDBRequest = objectStore.put(encryptedSelectedMerchant, key);

      request.onsuccess = (e: any) => {
        db.close();
        resolve(true);
      };

      request.onerror = (e: any) => {
        console.error(`Error to request AddSelectedMerchantToIndexedDB ${e.target.error}`);
        db.close();
        resolve(false);
      };
    } else {
      resolve(false);
    }
  });
};

export const GetSelectedMerchantFromIndexedDB = (key: IDBValidKey): Promise<SelectedMerchantModel | null> => {
  return new Promise(async (resolve, reject) => {
    const db: IDBDatabase = await OpenCacheIndexedDB();

    if (db) {
      const objectStore: IDBObjectStore = db
        .transaction([ObjectStore.SelectedMerchant], IDBTransactionMode.ReadOnly)
        .objectStore(ObjectStore.SelectedMerchant);
      const request: IDBRequest = objectStore.get(key);

      request.onsuccess = (e: any) => {
        db.close();

        if (request.result) {
          const selectedMerchant: SelectedMerchantModel = JSON.parse(DecryptionIndexedDbCash(request.result));
          resolve(selectedMerchant);
        } else {
          resolve(null);
        }
      };

      request.onerror = (e: any) => {
        console.error(`Error to request GetSelectedMerchantFromIndexedDB ${e.target.error}`);
        db.close();
        resolve(null);
      };
    } else {
      resolve(null);
    }
  });
};

export const DeleteSelectedMerchantFromIndexedDB = (key: IDBValidKey): Promise<boolean> => {
  return new Promise(async (resolve, reject) => {
    const db: IDBDatabase = await OpenCacheIndexedDB();

    if (db) {
      const objectStore: IDBObjectStore = db
        .transaction([ObjectStore.SelectedMerchant], IDBTransactionMode.ReadWrite)
        .objectStore(ObjectStore.SelectedMerchant);
      const request: IDBRequest = objectStore.delete(key);

      request.onsuccess = (e: any) => {
        db.close();
        resolve(true);
      };

      request.onerror = (e: any) => {
        console.error(`Error to request DeleteSelectedMerchantFromIndexedDB ${e.target.error}`);
        db.close();
        resolve(false);
      };
    } else {
      resolve(false);
    }
  });
};

export const ClearSelectedMerchantFromIndexedDB = (): Promise<boolean> => {
  return new Promise(async (resolve, reject) => {
    const db: IDBDatabase = await OpenCacheIndexedDB();

    if (db) {
      const objectStore: IDBObjectStore = db
        .transaction([ObjectStore.SelectedMerchant], IDBTransactionMode.ReadWrite)
        .objectStore(ObjectStore.SelectedMerchant);
      const request: IDBRequest = objectStore.clear();

      request.onsuccess = (e: any) => {
        db.close();
        resolve(true);
      };

      request.onerror = (e: any) => {
        db.close();
        resolve(false);
      };
    } else {
      resolve(false);
    }
  });
};
// #endregion
// #region ContactTaxIdLogo
export const AddContactTaxIdLogoToIndexedDB = (key: IDBValidKey, logoUrl: any): Promise<boolean> => {
  return new Promise(async (resolve, reject) => {
    const db: IDBDatabase = await OpenCacheIndexedDB();
    if (db) {
      const objectStore: IDBObjectStore = db
        .transaction([ObjectStore.ContactTaxIdLogo], IDBTransactionMode.ReadWrite)
        .objectStore(ObjectStore.ContactTaxIdLogo);
      const request: IDBRequest = objectStore.get(key);

      request.onsuccess = (event) => {
        const encryptDataCash = EncryptionIndexedDbCash(JSON.stringify(logoUrl));
        if (request.result) {
          const updateRequest = objectStore.put(encryptDataCash, key);
          updateRequest.onsuccess = () => {
            db.close();
            resolve(true);
          };

          updateRequest.onerror = (err) => {
            console.error(`Error to updateRequest AddContactTaxIdLogoToIndexedDB: ${err}`);
            db.close();
            resolve(false);
          };
        } else {
          const addRequest = objectStore.add(encryptDataCash, key);

          addRequest.onsuccess = (event) => {
            db.close();
            resolve(true);
          };

          addRequest.onerror = (err) => {
            console.error(`Error to addRequest AddContactTaxIdLogoToIndexedDB: ${err}`);
            db.close();
            resolve(false);
          };
        }
      };

      request.onerror = (err) => {
        console.error(`Error to request AddContactTaxIdLogoToIndexedDB: ${err}`);
        resolve(false);
      };
    } else {
      resolve(false);
    }
  });
};

export const GetContactTaxIdLogoFromIndexedDB = (key: IDBValidKey): Promise<any | null> => {
  return new Promise(async (resolve, reject) => {
    const db: IDBDatabase = await OpenCacheIndexedDB();
    if (db) {
      const objectStore: IDBObjectStore = db
        .transaction([ObjectStore.ContactTaxIdLogo], IDBTransactionMode.ReadWrite)
        .objectStore(ObjectStore.ContactTaxIdLogo);
      const request: IDBRequest = objectStore.get(key);

      request.onsuccess = (event) => {
        db.close();
        if (request.result) {
          const logoUrl = JSON.parse(DecryptionIndexedDbCash(request.result));
          resolve(logoUrl);
        } else {
          resolve("");
        }
      };

      request.onerror = (err) => {
        console.error(`Error to request GetContactTaxIdLogoFromIndexedDB: ${err}`);
        resolve(0);
      };
    } else {
      resolve(null);
    }
  });
};

export const DeleteContactTaxIdLogoFromIndexedDB = (key: IDBValidKey): Promise<boolean> => {
  return new Promise(async (resolve, reject) => {
    const db: IDBDatabase = await OpenCacheIndexedDB();
    if (db) {
      const objectStore: IDBObjectStore = db
        .transaction([ObjectStore.ContactTaxIdLogo], IDBTransactionMode.ReadWrite)
        .objectStore(ObjectStore.ContactTaxIdLogo);
      const request: IDBRequest = objectStore.delete(key);

      request.onsuccess = (event) => {
        db.close();
        resolve(true);
      };

      request.onerror = (err) => {
        console.error(`Error to request DeleteContactTaxIdLogoFromIndexedDB: ${err}`);
        resolve(false);
      };
    } else {
      resolve(false);
    }
  });
};

export const ClearContactTaxIdLogoFromIndexedDB = (): Promise<boolean> => {
  return new Promise(async (resolve, reject) => {
    const db: IDBDatabase = await OpenCacheIndexedDB();
    if (db) {
      const objectStore: IDBObjectStore = db
        .transaction([ObjectStore.ContactTaxIdLogo], IDBTransactionMode.ReadWrite)
        .objectStore(ObjectStore.ContactTaxIdLogo);
      const request: IDBRequest = objectStore.clear();

      request.onerror = (event: any) => {
        db.close();
        resolve(false);
      };

      request.onsuccess = (event: any) => {
        db.close();
        resolve(true);
      };
    } else {
      resolve(false);
    }
  });
};
// #endregion

export const ClearAllCacheFromIndexedDB = (): Promise<boolean> => {
  return new Promise(async (resolve, reject) => {
    await ClearAccessTokenFromIndexedDB();
    await ClearUserProfileFromIndexedDB();
    await ClearSelectedMerchantFromIndexedDB();
    await ClearContactTaxIdLogoFromIndexedDB();
    resolve(true);
  });
};
